// ─────────────────────────────────────────────────────────────
// RiskAreaIntro.js - Mantine Version
// Enhanced skip logic with Save for Later vs Not Applicable
// ─────────────────────────────────────────────────────────────

import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Transition,
  Tooltip,
  Divider,
} from '@mantine/core';
import {
  IconClock,
  IconX,
  IconRefresh,
} from '@tabler/icons-react';
import { 
  
  setAreaDeferred,
  setAreaNotApplicable,
  clearAreaStatus
} from '../redux/actions/riskActions';
import { ContinueButton } from '../components/NavigationButtons';

/**
 * Props
 * ─────────────────────────────────────────
 * modelId         – uuid of model
 * riskArea        – canonical key used in Redux (e.g. "Misinformation")
 * riskAreaLabel   – pretty title to show (optional; fall back to key)
 * assessmentUuid  – uuid for persistence
 * onNext()        – advance to the sources screen
 * onSkipToggle()  – legacy callback for skip toggle
 */
export default function RiskAreaIntro({
  modelId,
  riskArea,
  riskAreaLabel = riskArea,
  assessmentUuid,
  onNext,
  onSkipToggle,
}) {
  const dispatch = useDispatch();

  // Get risk area description from config
  const riskAreaConfig = useSelector((s) => s.config.configs['riskSources']?.[riskArea]);
  const riskAreaDescription = riskAreaConfig?.description || `Risks related to ${riskAreaLabel.toLowerCase()}.`;

  // Check current area status
  const areaStatus = useSelector(
    (s) => s.risks.models[modelId]?.riskFormData?.[riskArea]?.status
  );
  
  const isNotApplicable = areaStatus === 'NA';
  const isDeferred = areaStatus === 'DEFERRED';
  const isSkipped = isNotApplicable || isDeferred;

  // Check if there's existing progress to determine button text
  const hasExistingProgress = useSelector((s) => {
    const area = s.risks.models[modelId]?.riskFormData?.[riskArea];
    if (!area) return false;
    
    return (
      // Has selected sources
      (area.selectedSources?.length || 0) > 0 ||
      // Has source values selected
      area.riskSources?.some(src => 
        Array.isArray(src.value) ? src.value.length > 0 : !!src.value
      ) ||
      // Has any risks (saved, draft, or generated)
      Object.values(area.sectionRisks || {}).flat().length > 0 ||
      // Has assessment progress
      (area.assessmentProgress || 0) > 0
    );
  });

  const handleNotApplicable = () => {
    dispatch(setAreaNotApplicable(modelId, riskArea, assessmentUuid));
    
  };

  const handleSaveForLater = () => {
    dispatch(setAreaDeferred(modelId, riskArea, assessmentUuid));
    
  };

  const handleReEnable = () => {
    dispatch(clearAreaStatus(modelId, riskArea, assessmentUuid));
    
  };

  return (
    <Container fluid px="xl" py="xl">
      <Transition mounted transition="fade" duration={400}>
        {(styles) => (
          <Paper
            style={styles}
            withBorder
            radius="md"
            p="xl"
            sx={(theme) => ({
              border: `1px solid ${theme.colors.gray[2]}`,
              backgroundColor: theme.white,
              boxShadow: theme.shadows.xs,
            })}
          >
            <Stack spacing="xl">
              {/* Risk Area Header */}
              <Stack spacing="sm">
                <Title 
                  order={2} 
                  sx={(theme) => ({
                    fontWeight: 600,
                    color: theme.colors.dark[8],
                    fontSize: '1.75rem',
                  })}
                >
                  Risk Area: {riskAreaLabel}
                </Title>
                
                <Text 
                  size="md" 
                  c="dimmed"
                  sx={{ lineHeight: 1.6 }}
                >
                  {riskAreaDescription}
                </Text>
              </Stack>

              <Divider />

              {/* Status Messaging */}
              {isSkipped && (
                <Transition mounted={isSkipped} transition="slide-up" duration={300}>
                  {(styles) => (
                    <Paper
                      style={styles}
                      p="md"
                      radius="md"
                      sx={(theme) => ({
                        backgroundColor: isNotApplicable 
                          ? theme.colors.gray[0] 
                          : theme.colors.yellow[0],
                        border: `1px solid ${
                          isNotApplicable 
                            ? theme.colors.gray[2] 
                            : theme.colors.yellow[2]
                        }`,
                      })}
                    >
                      <Text size="sm" weight={500}>
                        {isNotApplicable && (
                          <Text c="gray.7">
                            This risk area is marked as <strong>Not Applicable</strong>. 
                            Click "Re-enable Risk Area" to include it in your assessment.
                          </Text>
                        )}
                        {isDeferred && (
                          <Text c="yellow.8">
                            This risk area is <strong>Saved for Later</strong>. 
                            Click "Re-enable Risk Area" to resume assessment.
                          </Text>
                        )}
                      </Text>
                    </Paper>
                  )}
                </Transition>
              )}

              {/* Action Buttons */}
              <Stack spacing="md">
                <Text size="sm" weight={500} c="dark.6" align="left">
                  Choose how to proceed with this risk area:
                </Text>
                
                <Group spacing="md" align="flex-start">
                  {/* Start/Continue Button */}
                  <ContinueButton
                    onClick={onNext}
                    disabled={isSkipped}
                  >
                    {hasExistingProgress ? 'Continue Assessment' : 'Start Assessment'}
                  </ContinueButton>

                  {/* Save for Later Button */}
                  {!isSkipped && (
                    <Tooltip 
                      label="Mark this area to return to later in the assessment"
                      withArrow
                    >
                      <Button
                        variant="light"
                        color="blue"
                        leftIcon={<IconClock size={16} />}
                        onClick={handleSaveForLater}
                        size="md"
                      >
                        Save for Later
                      </Button>
                    </Tooltip>
                  )}

                  {/* Not Applicable Button */}
                  {!isSkipped && (
                    <Tooltip 
                      label="Mark this area as not relevant to this assessment"
                      withArrow
                    >
                      <Button
                        variant="light"
                        color="gray"
                        leftIcon={<IconX size={16} />}
                        onClick={handleNotApplicable}
                        size="md"
                      >
                        Not Applicable
                      </Button>
                    </Tooltip>
                  )}

                  {/* Re-enable Button */}
                  {isSkipped && (
                    <Button
                      variant="filled"
                      color="gray"
                      leftIcon={<IconRefresh size={16} />}
                      onClick={handleReEnable}
                      size="md"
                    >
                      Re-enable Risk Area
                    </Button>
                  )}
                </Group>
              </Stack>
            </Stack>
          </Paper>
        )}
      </Transition>
    </Container>
  );
}